import { evaluatePermission } from "../utils/rbacPermissionCheck";

/**
 * Frontend RBAC vocabulary and route → permission mapping.
 * Backend registry: `_one_hub_backend/src/casbin/permissions.js` (merged from `tenantPermissionCatalog.js`).
 * Sync route list with `_one_hub_backend/src/casbin/appRoutePermissions.js` (permissions-registry API).
 * Use `toBackendAction` / `toBackendPermissionKey` when comparing to API summary permissions.
 *
 * Tenant pages ↔ API modules (Casbin):
 * - /dashboard → dashboard:view (legacy combined reports:view also grants dashboard, not sales/order_reports:*)
 * - /orders → orders:view | orders:update (GET/PATCH orders; status changes incl. cancel/reject)
 * - /inventory → inventory:view | inventory:update (all mutations) | inventory:enable_disable_inventory
 *   (+ inventory:enable_disable_inventory for auto-accept toggle API)
 * - /category-overview → categories:* ; /catalog*, /catalog-overview → catalog:*
 * - /products → products:*
 * - /store&branches-overview, /platforms, /catalog/apps-list → settings:* (stores & branches)
 * - /apps-management, add-app, add-on flows → app_management:view_apps | view_integrations | add_integrations | disconnect_integration
 * - /subscription and My Account billing blocks → subscriptions:view_subscription | perform_subscription | cancel_subscription
 * - /user-management → users:* (staff users)
 * - /rbac/roles → roles:* (grant roles:view separately; staff user menu hides Roles unless roles:* appears in access-summary)
 * - /sales-reports → sales_reports:view | sales_reports:export (legacy roles: reports:*)
 * - /reports → order_reports:view | order_reports:export (legacy roles: reports:*)
 * - /shipment-reports → shipment_reports:view | shipment_reports:export
 * - /shipments → shipments:view | shipments:create | shipments:cancel | shipments:export
 * - /trackhub/:id → trackhub_settings:view | trackhub_settings:update
 * - /printer-settings → printer_settings:*
 * - /admin/* → no tenant RBAC (platform admin only)
 * - payments:* → implied in permissions-registry only (no App route yet)
 *
 * Staff POST/PUT/PATCH/DELETE requests should include storeId/branchId (query or body) when
 * scoped; use `setTenantApiScope` in `services/index.js` so axios adds defaults.
 */

/** @typedef {{ module: string|null, action: string|null, anyOf?: [string, string][] }} RoutePermissionMatch */

export const PERMISSION_ACTIONS = Object.freeze({
  VIEW: "view",
  ADD: "add",
  EDIT: "edit",
  DELETE: "delete",
  EXPORT: "export",
  IMPORT: "import",
  SYNC: "sync",
  VIEW_APPS: "view_apps",
  VIEW_INTEGRATIONS: "view_integrations",
  ADD_INTEGRATIONS: "add_integrations",
  DISCONNECT_INTEGRATION: "disconnect_integration",
  MANAGE_AUTO_ACCESS_ORDERS: "manage_auto_access_orders",
  MANAGE_BRANCH_MAPPINGS: "manage_branch_mappings",
  MANAGE_ADDON_SETTINGS: "manage_addon_settings",
  ENABLE_DISABLE_INVENTORY: "enable_disable_inventory",
  VIEW_SUBSCRIPTION: "view_subscription",
  PERFORM_SUBSCRIPTION: "perform_subscription",
  CANCEL_SUBSCRIPTION: "cancel_subscription",
  CREATE: "create",
  CANCEL: "cancel",
});

export const RBAC_MODULES = Object.freeze({
  DASHBOARD: "dashboard",
  ORDERS: "orders",
  PAYMENTS: "payments",
  MENUS: "menus",
  CATALOG: "catalog",
  CATEGORIES: "categories",
  PRODUCTS: "products",
  INVENTORY: "inventory",
  USERS: "users",
  ROLES: "roles",
  REPORTS: "reports",
  SALES_REPORTS: "sales_reports",
  ORDER_REPORTS: "order_reports",
  SETTINGS: "settings",
  APP_MANAGEMENT: "app_management",
  PRINTER_SETTINGS: "printer_settings",
  ACCOUNT: "account",
  SUBSCRIPTIONS: "subscriptions",
  TRACKHUB_SETTINGS: "trackhub_settings",
  SHIPMENTS: "shipments",
  SHIPMENT_REPORTS: "shipment_reports",
});

/**
 * Map UI action names to Casbin actions for a module.
 * @param {string} module
 * @param {string} feAction view | add | edit | delete | export
 * @returns {string}
 */
export function toBackendAction(module, feAction) {
  const mod = String(module || "").toLowerCase();
  const a = String(feAction || "").toLowerCase();
  if (mod === RBAC_MODULES.APP_MANAGEMENT) {
    if (
      a === PERMISSION_ACTIONS.VIEW_APPS ||
      a === PERMISSION_ACTIONS.VIEW_INTEGRATIONS ||
      a === PERMISSION_ACTIONS.ADD_INTEGRATIONS ||
      a === PERMISSION_ACTIONS.DISCONNECT_INTEGRATION
    ) {
      return a;
    }
  }
  if (mod === RBAC_MODULES.SUBSCRIPTIONS) {
    if (
      a === PERMISSION_ACTIONS.VIEW_SUBSCRIPTION ||
      a === PERMISSION_ACTIONS.PERFORM_SUBSCRIPTION ||
      a === PERMISSION_ACTIONS.CANCEL_SUBSCRIPTION
    ) {
      return a;
    }
  }
  if (mod === RBAC_MODULES.SETTINGS) {
    if (
      a === PERMISSION_ACTIONS.MANAGE_AUTO_ACCESS_ORDERS ||
      a === PERMISSION_ACTIONS.MANAGE_BRANCH_MAPPINGS ||
      a === PERMISSION_ACTIONS.MANAGE_ADDON_SETTINGS
    ) {
      return a;
    }
  }
  if (mod === RBAC_MODULES.INVENTORY) {
    if (a === PERMISSION_ACTIONS.ENABLE_DISABLE_INVENTORY) {
      return a;
    }
  }
  if (a === "invite" || a === "import" || a === "sync") return a;
  if (a === PERMISSION_ACTIONS.ADD) {
    if (
      mod === RBAC_MODULES.MENUS ||
      mod === RBAC_MODULES.USERS ||
      mod === RBAC_MODULES.ROLES ||
      mod === RBAC_MODULES.CATALOG ||
      mod === RBAC_MODULES.CATEGORIES ||
      mod === RBAC_MODULES.PRODUCTS
    ) {
      return "create";
    }
    if (mod === RBAC_MODULES.SETTINGS || mod === RBAC_MODULES.PRINTER_SETTINGS) {
      return "create";
    }
    if (
      mod === RBAC_MODULES.ORDERS ||
      mod === RBAC_MODULES.INVENTORY ||
      mod === RBAC_MODULES.PAYMENTS ||
      mod === RBAC_MODULES.REPORTS ||
      mod === RBAC_MODULES.SALES_REPORTS ||
      mod === RBAC_MODULES.ORDER_REPORTS ||
      mod === RBAC_MODULES.DASHBOARD
    ) {
      return "update";
    }
    return "create";
  }
  if (a === PERMISSION_ACTIONS.EDIT) return "update";
  return a;
}

/**
 * @param {string} module
 * @param {string} feAction
 * @returns {string} e.g. `menus:create`
 */
export function toBackendPermissionKey(module, feAction) {
  return `${module}:${toBackendAction(module, feAction)}`;
}

/**
 * Ordered rules — first match wins (keep dynamic/specific paths before generic ones).
 * `module` / `action` null → any authenticated user (still behind `PrivateRoute`).
 */
export const ROUTE_PERMISSION_RULES = Object.freeze([
  // Public — same paths as App.js
  { re: /^\/$/, module: null, action: null },
  { re: /^\/register$/, module: null, action: null },
  { re: /^\/terms-and-conditions$/, module: null, action: null },
  { re: /^\/forgot-password$/, module: null, action: null },
  { re: /^\/reset-password\/[^/]+$/, module: null, action: null },
  { re: /^\/partner-sign-in$/, module: null, action: null },
  { re: /^\/partner-sessions\/[^/]+$/, module: null, action: null },
  // Platform admin — not tenant RBAC
  { re: /^\/admin\/login$/, module: null, action: null },
  { re: /^\/admin\/dashboard$/, module: null, action: null },
  { re: /^\/admin\/user-list$/, module: null, action: null },
  { re: /^\/admin\/available-apps$/, module: null, action: null },
  { re: /^\/admin\/add-app$/, module: null, action: null },
  { re: /^\/admin\/manage-routes\/[^/]+$/, module: null, action: null },
  { re: /^\/admin\/edit-app$/, module: null, action: null },
  { re: /^\/admin\/system-logs$/, module: null, action: null },
  { re: /^\/admin\/activity-logs$/, module: null, action: null },
  { re: /^\/admin\/shipments$/, module: null, action: null },
  { re: /^\/admin\/profile$/, module: null, action: null },
  // Tenant — order aligned with App.js protected routes
  { re: /^\/dashboard\/?$/, module: RBAC_MODULES.DASHBOARD, action: PERMISSION_ACTIONS.VIEW },
  { re: /^\/setup-guide$/, module: RBAC_MODULES.DASHBOARD, action: PERMISSION_ACTIONS.VIEW },
  { re: /^\/platforms$/, module: RBAC_MODULES.SETTINGS, action: PERMISSION_ACTIONS.VIEW },

  { re: /^\/orders$/, module: RBAC_MODULES.ORDERS, action: PERMISSION_ACTIONS.VIEW },
  { re: /^\/orders-display$/, module: RBAC_MODULES.ORDERS, action: PERMISSION_ACTIONS.VIEW },

  { re: /^\/catalog-overview$/, module: RBAC_MODULES.CATALOG, action: PERMISSION_ACTIONS.VIEW },
  { re: /^\/category-overview$/, module: RBAC_MODULES.CATEGORIES, action: PERMISSION_ACTIONS.VIEW },

  { re: /^\/store&branches-overview$/, module: RBAC_MODULES.SETTINGS, action: PERMISSION_ACTIONS.VIEW },

  { re: /^\/inventory$/, module: RBAC_MODULES.INVENTORY, action: PERMISSION_ACTIONS.VIEW },

  { re: /^\/products-listing$/, module: RBAC_MODULES.PRODUCTS, action: PERMISSION_ACTIONS.VIEW },

  { re: /^\/sales-reports$/, module: RBAC_MODULES.SALES_REPORTS, action: PERMISSION_ACTIONS.VIEW },
  { re: /^\/reports$/, module: RBAC_MODULES.ORDER_REPORTS, action: PERMISSION_ACTIONS.VIEW },
  { re: /^\/shipment-reports$/, module: RBAC_MODULES.SHIPMENT_REPORTS, action: PERMISSION_ACTIONS.VIEW },
  { re: /^\/shipments\/new$/, module: RBAC_MODULES.SHIPMENTS, action: PERMISSION_ACTIONS.CREATE },
  { re: /^\/shipments$/, module: RBAC_MODULES.SHIPMENTS, action: PERMISSION_ACTIONS.VIEW },

  { re: /^\/catalog\/items-list$/, module: RBAC_MODULES.CATALOG, action: PERMISSION_ACTIONS.VIEW },
  { re: /^\/catalog\/apps-list$/, module: RBAC_MODULES.SETTINGS, action: PERMISSION_ACTIONS.VIEW },

  { re: /^\/orders\/[^/]+$/, module: RBAC_MODULES.ORDERS, action: PERMISSION_ACTIONS.VIEW },

  {
    re: /^\/apps-management$/,
    module: null,
    action: null,
    anyOf: [
      [RBAC_MODULES.APP_MANAGEMENT, PERMISSION_ACTIONS.VIEW_APPS],
      [RBAC_MODULES.APP_MANAGEMENT, PERMISSION_ACTIONS.VIEW_INTEGRATIONS],
    ],
  },
  {
    re: /^\/subscription$/,
    module: RBAC_MODULES.SUBSCRIPTIONS,
    action: PERMISSION_ACTIONS.VIEW_SUBSCRIPTION,
  },
  { re: /^\/user-management$/, module: RBAC_MODULES.USERS, action: PERMISSION_ACTIONS.VIEW },
  { re: /^\/rbac\/roles$/, module: RBAC_MODULES.ROLES, action: PERMISSION_ACTIONS.VIEW },

  { re: /^\/services\/[^/]+$/, module: RBAC_MODULES.APP_MANAGEMENT, action: PERMISSION_ACTIONS.VIEW_APPS },
  { re: /^\/add-app\/[^/]+$/, module: RBAC_MODULES.APP_MANAGEMENT, action: PERMISSION_ACTIONS.ADD_INTEGRATIONS },
  { re: /^\/addon-intermediary\/[^/]+$/, module: RBAC_MODULES.APP_MANAGEMENT, action: PERMISSION_ACTIONS.VIEW_APPS },
  { re: /^\/addon-integration\/[^/]+$/, module: RBAC_MODULES.APP_MANAGEMENT, action: PERMISSION_ACTIONS.ADD_INTEGRATIONS },
  { re: /^\/trackhub\/[^/]+$/, module: RBAC_MODULES.TRACKHUB_SETTINGS, action: PERMISSION_ACTIONS.VIEW },
  { re: /^\/trackhub-integration\/[^/]+$/, module: RBAC_MODULES.APP_MANAGEMENT, action: PERMISSION_ACTIONS.ADD_INTEGRATIONS },
  { re: /^\/trackhub-reports\/[^/]+$/, module: RBAC_MODULES.SHIPMENTS, action: PERMISSION_ACTIONS.VIEW },

  { re: /^\/oauth\/keeta\/callback$/, module: null, action: null },
  { re: /^\/redirect-foodics$/, module: null, action: null },

  { re: /^\/add-category$/, module: RBAC_MODULES.CATEGORIES, action: PERMISSION_ACTIONS.ADD },
  { re: /^\/add-item$/, module: RBAC_MODULES.PRODUCTS, action: PERMISSION_ACTIONS.ADD },
  { re: /^\/add-product$/, module: RBAC_MODULES.PRODUCTS, action: PERMISSION_ACTIONS.ADD },
  { re: /^\/edit-catalog$/, module: RBAC_MODULES.CATALOG, action: PERMISSION_ACTIONS.EDIT },
  { re: /^\/add-catalog$/, module: RBAC_MODULES.CATALOG, action: PERMISSION_ACTIONS.ADD },

  { re: /^\/add-store$/, module: RBAC_MODULES.SETTINGS, action: PERMISSION_ACTIONS.ADD },
  { re: /^\/edit-store$/, module: RBAC_MODULES.SETTINGS, action: PERMISSION_ACTIONS.EDIT },
  { re: /^\/add-branch$/, module: RBAC_MODULES.SETTINGS, action: PERMISSION_ACTIONS.ADD },

  { re: /^\/menu$/, module: RBAC_MODULES.CATALOG, action: PERMISSION_ACTIONS.VIEW },

  { re: /^\/branches$/, module: RBAC_MODULES.SETTINGS, action: PERMISSION_ACTIONS.VIEW },
  { re: /^\/manage-branches-v2$/, module: RBAC_MODULES.SETTINGS, action: PERMISSION_ACTIONS.VIEW },
  { re: /^\/manage-branches$/, module: RBAC_MODULES.SETTINGS, action: PERMISSION_ACTIONS.VIEW },

  { re: /^\/profile$/, module: null, action: null },
  { re: /^\/setup-2fa$/, module: null, action: null },
  { re: /^\/my-account$/, module: RBAC_MODULES.ACCOUNT, action: PERMISSION_ACTIONS.VIEW },

  { re: /^\/printer-settings\/[^/]+\/edit$/, module: RBAC_MODULES.PRINTER_SETTINGS, action: PERMISSION_ACTIONS.EDIT },
  { re: /^\/printer-settings\/new$/, module: RBAC_MODULES.PRINTER_SETTINGS, action: PERMISSION_ACTIONS.ADD },
  { re: /^\/printer-settings$/, module: RBAC_MODULES.PRINTER_SETTINGS, action: PERMISSION_ACTIONS.VIEW },

  { re: /^\/products$/, module: RBAC_MODULES.PRODUCTS, action: PERMISSION_ACTIONS.VIEW },
  { re: /^\/product\/[^/]+$/, module: RBAC_MODULES.PRODUCTS, action: PERMISSION_ACTIONS.VIEW },

  { re: /^\/catalog\/[^/]+$/, module: RBAC_MODULES.CATALOG, action: PERMISSION_ACTIONS.VIEW },
  { re: /^\/catalog$/, module: RBAC_MODULES.CATALOG, action: PERMISSION_ACTIONS.VIEW },

  { re: /^\/view-branch$/, module: RBAC_MODULES.SETTINGS, action: PERMISSION_ACTIONS.VIEW },
  { re: /^\/view-branch-v1$/, module: RBAC_MODULES.SETTINGS, action: PERMISSION_ACTIONS.VIEW },
  { re: /^view-branch$/, module: RBAC_MODULES.SETTINGS, action: PERMISSION_ACTIONS.VIEW },
  { re: /^view-branch-v1$/, module: RBAC_MODULES.SETTINGS, action: PERMISSION_ACTIONS.VIEW },
]);

/**
 * @param {string} pathname
 * @returns {RoutePermissionMatch}
 */
export function matchRoutePermission(pathname) {
  const p = (pathname || "").split("?")[0];
  for (const rule of ROUTE_PERMISSION_RULES) {
    if (rule.re.test(p)) {
      return {
        module: rule.module,
        action: rule.action,
        anyOf: rule.anyOf || undefined,
      };
    }
  }
  return { module: RBAC_MODULES.REPORTS, action: PERMISSION_ACTIONS.VIEW };
}

/**
 * @param {string} pathname
 * @param {(module: string, action: string) => boolean} canFn
 * @param {(checks: [string, string][]) => boolean} [canAnyFn]
 */
export function isRouteAllowed(pathname, canFn, canAnyFn) {
  const { module, action, anyOf } = matchRoutePermission(pathname);
  if (module == null && action == null) {
    if (anyOf?.length) {
      return canAnyFn ? canAnyFn(anyOf) : anyOf.some(([m, a]) => canFn(m, a));
    }
    return true;
  }
  if (anyOf?.length) {
    return canAnyFn ? canAnyFn(anyOf) : anyOf.some(([m, a]) => canFn(m, a));
  }
  return canFn(module, action);
}

/**
 * Tenant sidebar order — first match wins for post-login / forbidden-route landing.
 * Keep aligned with `SideNavbar.js` menuItems + parent submenu paths.
 */
export const TENANT_LANDING_ROUTE_ORDER = Object.freeze([
  "/dashboard",
  "/orders",
  "/shipments",
  "/inventory",
  "/store&branches-overview",
  "/apps-management",
  "/subscription",
  "/user-management",
  "/rbac/roles",
  "/category-overview",
  "/products",
  "/catalog",
  "/sales-reports",
  "/reports",
  "/shipment-reports",
  "/printer-settings",
]);

const TENANT_LANDING_FALLBACK = "/profile";

/**
 * @param {(module: string, action: string) => boolean} canFn
 * @returns {string}
 */
function firstPathFromCan(canFn, canAnyFn) {
  for (const path of TENANT_LANDING_ROUTE_ORDER) {
    if (isRouteAllowed(path, canFn, canAnyFn)) return path;
  }
  return TENANT_LANDING_FALLBACK;
}

/**
 * First tenant route the user may view (staff landing when dashboard is denied).
 * Pass access-summary object right after `refresh()` so checks are not stale before React re-renders.
 * @param {((module: string, action: string) => boolean)|{ permissions?: string[] }|null|undefined} canOrSummary
 * @returns {string}
 */
export function getFirstAccessibleTenantPath(canOrSummary) {
  if (typeof canOrSummary === "function") {
    return firstPathFromCan(canOrSummary);
  }
  const canFn = (module, action) => evaluatePermission(canOrSummary, module, action);
  const canAnyFn = (checks) => checks.some(([m, a]) => canFn(m, a));
  return firstPathFromCan(canFn, canAnyFn);
}
